import {
  addressSchema,
  signatureUrlSchema,
  workSchedulesSchema,
} from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';

const getOfficeDoctorItemResponseSchema = z.object({
  id: z.string().uuid(),
  firstname: z.string(),
  lastname: z.string(),
  phoneNumber: z.string().nullable().default(null),
  orderRegistrationNumber: z.string(),
  rhythm: z.string().nullable().default(null),
  iban: z.string().nullable().default(null),
  bic: z.string().nullable().default(null),
  signatureUrl: signatureUrlSchema,
  workSchedulesDateRef: z.string().nullable(),
  workSchedules: workSchedulesSchema,
  createdAt: z.coerce.date(),
  /** Set once the doctor left the company; kept so past slots still resolve. */
  deactivatedAt: z.coerce.date().nullable().default(null),
});

export type OfficeDoctorAttendingItem = z.infer<
  typeof getOfficeDoctorItemResponseSchema
>;

const getOfficeResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nationalMedicalOrderMail: z.string(),
  address: addressSchema,
  estimatedCaPerSlot: z.coerce.number(),
  averageSlotHonorary: z.coerce.number().default(0),
  withAssistantRetrocessionRate: z.number(),
  withoutAssistantRetrocessionRate: z.number(),
  weekendRetrocessionRate: z.number(),
  createdAt: z.coerce.date(),
  siteId: z.string().uuid(),
  open: z.boolean().default(true),
  /** Set when the practice closed: reads keep working, writes are refused. */
  deactivatedAt: z.coerce.date().nullable().default(null),
  assignedCsm: z
    .object({
      id: z.string().uuid(),
      firstname: z.string(),
      lastname: z.string(),
    })
    .nullable()
    .default(null),
  doctors: z.array(getOfficeDoctorItemResponseSchema).optional().default([]),
});

export type GetOfficeResponse = z.infer<typeof getOfficeResponseSchema>;

const getOffice = (id: string) => async () => {
  return http.offices
    .getDetails(id)
    .then<GetOfficeResponse>(({ response, transform }) => {
      const result = transform.handle({
        response,
        attributes: {
          name: true,
          nationalMedicalOrderMail: true,
          address: true,
          estimatedCaPerSlot: true,
          averageSlotHonorary: true,
          withAssistantRetrocessionRate: true,
          withoutAssistantRetrocessionRate: true,
          weekendRetrocessionRate: true,
          createdAt: true,
          siteId: true,
          open: true,
          deactivatedAt: true,
          assignedCsm: true,
        },
        relations: {
          doctors: true,
        },
        schema: getOfficeResponseSchema,
      });

      return {
        id: result.id,
        name: result.name,
        nationalMedicalOrderMail: result.nationalMedicalOrderMail,
        address: result.address,
        estimatedCaPerSlot: result.estimatedCaPerSlot,
        averageSlotHonorary: result.averageSlotHonorary,
        withAssistantRetrocessionRate: result.withAssistantRetrocessionRate,
        withoutAssistantRetrocessionRate:
          result.withoutAssistantRetrocessionRate,
        weekendRetrocessionRate: result.weekendRetrocessionRate,
        createdAt: result.createdAt,
        siteId: result.siteId,
        open: result.open,
        deactivatedAt: result.deactivatedAt,
        assignedCsm: result.assignedCsm,
        doctors: result.doctors || [],
      };
    });
};

export const useGetOfficeQuery = (
  id: string,
  enabled: boolean = true,
): UseQueryResult<GetOfficeResponse, Error> => {
  return useQuery({
    queryFn: getOffice(id),
    queryKey: ['office', id],
    retry: false,
    enabled,
  });
};
